import React, { useEffect, useState } from "react";
import moment from "moment";

import PanelTemplate from "../../../templates/Panel";
import Box from "../../../components/Box";
import InputDate from "../../../components/InputDate";
import InputSelect from "../../../components/InputSelect";
import IncomeStatement from "./statement";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { createOptions } from "../../../helpers/selects";
import {
    getBillingCompanyLabel,
    getStoredBillingCompany,
    storeBillingCompany,
    useBillingCompanies,
} from "../billingCompany";

const BASES = [
    { key: 'accrual', label: 'Competência' },
    { key: 'due', label: 'Vencimento' },
];

const PERIOD_PRESETS = [
    {
        key: 'this-year',
        label: 'Este ano',
        range: () => [moment().startOf('year'), moment().endOf('year')],
    },
    {
        key: 'last-12-months',
        label: 'Últimos 12 meses',
        range: () => [moment().subtract(11, 'months').startOf('month'), moment().endOf('month')],
    },
    {
        key: 'this-quarter',
        label: 'Este trimestre',
        range: () => [moment().startOf('quarter'), moment().endOf('quarter')],
    },
    {
        key: 'last-year',
        label: 'Ano passado',
        range: () => [moment().subtract(1, 'year').startOf('year'), moment().subtract(1, 'year').endOf('year')],
    },
];

const DEFAULT_PRESET = PERIOD_PRESETS[0];

export const FinanceIncomeStatement = () => {
    const [loading, setLoading] = useState(true);

    const [basis, setBasis] = useState('accrual');
    const [preset, setPreset] = useState(DEFAULT_PRESET.key);
    const [startDate, setStartDate] = useState(DEFAULT_PRESET.range()[0].format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(DEFAULT_PRESET.range()[1].format('YYYY-MM-DD'));
    const [costCenterId, setCostCenterId] = useState(null);
    const [billingCompanyId, setBillingCompanyId] = useState(getStoredBillingCompany());

    const { billingCompanies, hasMultiple: hasMultipleBillingCompanies } = useBillingCompanies();

    const [costCenters, setCostCenters] = useState([]);
    const [statement, setStatement] = useState(null);

    const periodIsValid = !!startDate && !!endDate && !moment(endDate).isBefore(startDate);

    useEffect(() => {
        getCostCenters();
    }, []);

    useEffect(() => {
        if (!periodIsValid) {
            return;
        }

        getStatement();
    }, [basis, startDate, endDate, costCenterId, billingCompanyId]);

    // Mesma autodefesa das outras telas do financeiro: empresa guardada que não
    // existe mais deixaria o relatório permanentemente vazio, sem explicação.
    useEffect(() => {
        if (billingCompanies.length === 0 || !billingCompanyId) return;
        if (billingCompanies.some(company => company.id === billingCompanyId)) return;

        storeBillingCompany('');
        setBillingCompanyId('');
    }, [billingCompanies]);

    const getCostCenters = () => {
        api.get(`/finance-cost-center`).then(res => {
            setCostCenters(res.data.resources.filter(costCenter => costCenter.active));
        }).catch(() => {});
    };

    const getStatement = () => {
        setLoading(true);

        const params = new URLSearchParams({
            start_date: startDate,
            end_date: endDate,
            basis,
        });

        if (costCenterId) {
            params.append('finance_cost_center_id', costCenterId);
        }

        if (billingCompanyId) {
            params.append('billing_company_id', billingCompanyId);
        }

        api.get(`/finance-report/income-statement?${params.toString()}`).then(res => {
            setStatement(res.data.resources);
        }).catch(renderError).then(() => setLoading(false));
    };

    const handleBillingCompany = id => {
        storeBillingCompany(id);
        setBillingCompanyId(id);
    };

    const handlePreset = presetToApply => {
        const [start, end] = presetToApply.range();

        setPreset(presetToApply.key);
        setStartDate(start.format('YYYY-MM-DD'));
        setEndDate(end.format('YYYY-MM-DD'));
    };

    // Mexer nas datas na mão desliga o atalho ativo.
    const handleStartDate = date => {
        setPreset(null);
        setStartDate(date);
    };

    const handleEndDate = date => {
        setPreset(null);
        setEndDate(date);
    };

    return (
        <PanelTemplate id="finance-income-statement">
            <div className="finance-income-statement">
                <Box>
                    <div className="finance-view-modes">
                        {BASES.map(item => (
                            <button
                                key={item.key}
                                type="button"
                                className={`view-mode${basis === item.key ? ' active' : ''}`}
                                onClick={() => setBasis(item.key)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    <span className="finance-basis-hint">
                        {basis === 'accrual'
                            ? 'Competência: o título no mês em que a receita ou a despesa aconteceu, pago ou não.'
                            : 'Vencimento: o título no mês em que ele vence. Para o dinheiro que de fato entrou ou saiu, use o fluxo de caixa.'
                        }
                    </span>

                    <div className="finance-period-presets">
                        {PERIOD_PRESETS.map(item => (
                            <button
                                key={item.key}
                                type="button"
                                className={`period-preset${preset === item.key ? ' active' : ''}`}
                                onClick={() => handlePreset(item)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    <div className="finance-filters">
                        {hasMultipleBillingCompanies &&
                            <div className="filter-field">
                                <InputSelect
                                    label={`Empresa`}
                                    options={[
                                        { label: 'Todas as empresas', value: '' },
                                        ...billingCompanies.map(company => ({ label: getBillingCompanyLabel(company), value: company.id })),
                                    ]}
                                    value={billingCompanyId}
                                    change={handleBillingCompany}
                                />
                            </div>
                        }
                        <div className="filter-field">
                            <InputDate label={`De`} format={`YYYY-MM-DD`} value={startDate} change={handleStartDate} />
                        </div>
                        <div className="filter-field">
                            <InputDate label={`Até`} format={`YYYY-MM-DD`} value={endDate} change={handleEndDate} />
                        </div>
                        <div className="filter-field">
                            <InputSelect
                                label={`Centro de custo`}
                                options={createOptions(costCenters, 'name', 'id', true, null, 'Todos os centros')}
                                value={costCenterId}
                                change={setCostCenterId}
                            />
                        </div>
                    </div>

                    {!periodIsValid &&
                        <span className="finance-period-error">
                            Informe as duas datas — a data final precisa ser igual ou posterior à inicial.
                        </span>
                    }
                </Box>

                <Box className={`no-padding`}>
                    <IncomeStatement data={statement} loading={loading} />
                </Box>
            </div>
        </PanelTemplate>
    );
};

export default FinanceIncomeStatement;
